// Campos sobre los que busca la Predictive Search API. Por defecto Shopify solo
// usa title, product_type, variants.title y vendor; agregamos tag y variants.sku
// para poder buscar por etiqueta y por SKU.
const PREDICTIVE_SEARCH_FIELDS = ['title', 'product_type', 'variants.title', 'vendor', 'tag', 'variants.sku'].join(',');

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.onSubmit.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  onSubmit(event) {
    const isOpen = this.classList.contains('predictive-search-open');
    const hasQuery = this.getQuery().length > 0;

    if (!isOpen) {
      event.preventDefault();
      this.input.focus();
      return;
    }
    if (!hasQuery) {
      event.preventDefault();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('resources[options][fields]', PREDICTIVE_SEARCH_FIELDS),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = this.querySelector(`[id="${id}"]`);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
    // Notify the mobile toggle so the revealed bar collapses in lockstep when the
    // predictive search is closed from any path (overlay click, etc.).
    if (!isOpen && this.isHeaderSearch) {
      document.dispatchEvent(new CustomEvent('header-search:close'));
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

class SearchToggle extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.header = this.closest('header');
    // Resolve the MOBILE predictive-search instance with a SCOPED selector.
    // (The snippet renders twice with duplicate ids, so never use getElementById here.)
    this.search = this.header?.querySelector('.header__search--mobile predictive-search');

    this.onClick = this.onClick.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.button?.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);
    // Collapse the bar whenever the predictive search closes (overlay click, etc.).
    document.addEventListener('header-search:close', this.close);
    // Opening the hamburger menu also closes search.
    document.addEventListener('menu-drawer:open', this.close);

    // Defensive: always start closed (handles bfcache restores).
    this.header?.classList.remove('mobile-search-open');
    document.body.classList.remove('search-open');
  }

  get isOpen() {
    return this.header?.classList.contains('mobile-search-open');
  }

  onClick(event) {
    event.preventDefault();
    event.stopPropagation(); // keep PredictiveSearch.handleClickOutside from reacting to this click
    this.isOpen ? this.close() : this.open();
  }

  open() {
    if (!this.search || this.isOpen) return;
    this.header.classList.add('mobile-search-open');
    // Force the shared "search open" state even when no recommendations are
    // configured (PredictiveSearch.onFocus only sets it when recommendations
    // exist). This turns off the transparent-header styling — otherwise the
    // input text/placeholder stay white on the now-opaque header — and adds the
    // dimmed overlay + scroll-lock so the experience matches the design.
    document.body.classList.add('search-open');
    this.button.setAttribute('aria-expanded', 'true');
    this.button.setAttribute('aria-label', this.button.dataset.labelClose);
    // Focusing the input runs the existing onFocus lifecycle:
    // predictive-search-open + viewport vars + recommendations.
    this.search.input?.focus();
  }

  close() {
    if (!this.isOpen) return;
    this.header.classList.remove('mobile-search-open');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-label', this.button.dataset.labelOpen);
    if (this.search) {
      // Reset without re-focusing the input (PredictiveSearch.clear() refocuses, which would reopen).
      this.search.input.value = '';
      this.search.removeAttribute('results');
      this.search.toggleSearchState(false);
      this.search.input.blur();
    }
    this.button.focus();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) this.close();
  }
}
customElements.define('search-toggle', SearchToggle);
